import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { Grid, Typography, Dialog, DialogContent, CircularProgress, Box, Tooltip } from '@mui/material'
import { useState, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { customFieldInputSx, machineSettingInputSx } from '../../Inputs/inputComponents/styles/CommonStyles';

const styles = {
    textSettingBox: {
        backgroundColor: 'var(--spectre-surface)',
        textArea: {
            color: 'var(--spectre-settings-text-secondary)',
        },
        mt: 1
    },
    text: {
        "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: 'var(--spectre-text-muted)',
        },
        input: {
            "&::placeholder": {
                opacity: 1,
            },
        },
    },
    error: {
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
                borderColor: "red"
            },
            "& fieldset": {
                borderColor: "red"
            },
            "&:hover fieldset": {
                borderColor: "red"
            }
        }
    }
}

export default function TextSetting(props: any) {
    const { element, display_name, updateMachineLocation, disabledState, key, inputType, passwordState, navigateToMatchmaking, machine } = props
    const [value, setValue] = useState<string>(element['value']);
    const [showPassword, setShowPassword] = useState(false);
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const isAdditionalParams = display_name === "Additional parameters";
    const { t } = useTranslation();

    // Format JSON on initial load
    useEffect(() => {
        if (isAdditionalParams && element['value']) {
            try {
                const parsedJson = JSON.parse(element['value']);
                const formattedJson = JSON.stringify(parsedJson, null, 2);
                setValue(formattedJson);
            } catch (e) {
                // Keep original value if parsing fails on load
                console.warn("Failed to parse initial JSON for Additional parameters");
            }
        }
    }, [element, isAdditionalParams]);

    const handleChange = function (event: React.ChangeEvent<HTMLInputElement>) {
        setValue(event.target.value);

        if (isAdditionalParams) {
            if (event.target.value.trim() === "") {
                setJsonError(null);
                return;
            }

            try {
                JSON.parse(event.target.value);
                setJsonError(null);
            } catch (e) {
                setJsonError("Invalid JSON format");
            }
        }
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const trimmedValue = e.target.value.trim();

        if (display_name === 'Location') {
            updateMachineLocation(trimmedValue);
        }

        if (display_name === 'Name') {
            machine.machineName = e.target.value;

            // Update the URL in ProxyConnection if it's being used
            if (machine.connection.type() === "Proxy") {
                setIsUpdating(true); // Show the modal

                element.Set(trimmedValue)
                    .then(() => {
                        return (machine.connection as any).updateMachineName(e.target.value);
                    })
                    .then(() => {
                        // Start pinging to check if the name change is complete
                        const startTime = Date.now();
                        const MAX_WAIT_TIME = 20000; // 20 seconds

                        const checkConnection = () => {
                            machine.connection.ping()
                                .then((response: Boolean) => {
                                    if (response) {
                                        setIsUpdating(false); // Hide the modal on success
                                    } else if (Date.now() - startTime < MAX_WAIT_TIME) {
                                        // Try again in 500ms if we haven't exceeded the timeout
                                        setTimeout(checkConnection, 500);
                                    } else {
                                        // Timeout occurred
                                        setIsUpdating(false);
                                        navigateToMatchmaking();
                                    }
                                })
                                .catch(() => {
                                    if (Date.now() - startTime < MAX_WAIT_TIME) {
                                        // Try again in 500ms if we haven't exceeded the timeout
                                        setTimeout(checkConnection, 500);
                                    } else {
                                        // Timeout occurred
                                        setIsUpdating(false);
                                        navigateToMatchmaking();
                                    }
                                });
                        };
                        checkConnection();
                    })
                    .catch(() => {
                        setIsUpdating(false);
                        navigateToMatchmaking();
                    });
                return;
            }
        }

        if (isAdditionalParams && trimmedValue !== "") {
            try {
                // Parse and re-stringify to normalize the JSON format
                const parsedJson = JSON.parse(trimmedValue);
                // Format for display
                const formattedJson = JSON.stringify(parsedJson, null, 2);
                setValue(formattedJson);

                // Save the compacted version for consistent storage
                element.Set(JSON.stringify(parsedJson))
                    .catch(() => {
                        navigateToMatchmaking();
                    });
            } catch (error) {
                // Don't save invalid JSON
                console.error("Invalid JSON:", error);
            }
        } else {
            // For non-JSON fields, proceed as before
            element.Set(trimmedValue)
                .catch(() => {
                    navigateToMatchmaking();
                });
        }
    }

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: any) => {
        event.preventDefault();
    };

    const iconAdornment = passwordState
        ? <InputAdornment position="end">
            <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
            >
                {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
        </InputAdornment>
        : ""

    const textInput = <>
        {
            isAdditionalParams ?
                <Typography sx={styles.textSettingBox.textArea}>
                    {display_name} :
                    {jsonError && <span style={{ color: 'red', marginLeft: '8px', fontSize: '0.85em' }}>{jsonError}</span>}
                </Typography>
                : null
        }
        <TextField
            type={showPassword ? "text" : inputType}
            variant="outlined"
            disabled={disabledState}
            size="small"
            fullWidth
            value={value}
            error={!!jsonError}
            inputProps={{
                style: isAdditionalParams ? { fontFamily: 'monospace' } : undefined,
                ...(display_name === 'AuthHeader' && {
                    autoComplete: 'off',
                    'data-form-type': 'other'
                })
            }}
            placeholder={isAdditionalParams ? "*Additional parameters should be in JSON format. Contact engineering@cargospectre.com for more details on these parameters." : ""}
            multiline={isAdditionalParams ? true : false}
            rows={isAdditionalParams ? 4 : 1}
            sx={jsonError
                ? { ...styles.text, ...customFieldInputSx, ...machineSettingInputSx, ...styles.error }
                : { ...styles.text, ...customFieldInputSx, ...machineSettingInputSx }}
            onChange={handleChange}
            onBlur={handleBlur}
            InputProps={
                !isAdditionalParams
                    ? {
                        autoComplete: 'new-password',
                        startAdornment: <InputAdornment sx={styles.textSettingBox.textArea} position="start">{display_name} : </InputAdornment>,
                        endAdornment: iconAdornment,
                    }
                    : undefined
            }
        />
    </>

    return (
        <>
            <Grid key={key} item xs={12} lg={6} sx={styles.textSettingBox}>
                {element.Tooltip && element.Tooltip !== "no name" ?
                    <Tooltip title={element.Tooltip} placement="top">
                        {/* The Box is necessary to ensure the tooltip appears when the control is disabled */}
                        <Box component="span" sx={{ display: 'block' }}>
                            {textInput}
                        </Box>
                    </Tooltip>
                    :
                    textInput
                }
            </Grid>
            {/* Modal dialog for updating machine name */}
            <Dialog open={isUpdating} disableEscapeKeyDown={true}>
                <DialogContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', p: 2 }}>
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography>{t('control.settings.pleaseWait')}</Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
}